import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type {
  TransfersListParams,
  TransfersListResult,
  TransfersHelpData,
  ApiTransfersListData,
  TransferDetailsResponse,
  ItemsDetailsResponse,
  GetItemsDetailsParams,
} from "@/features/transfers/types/transfers.types";
import type { CreateTransferRequestPayload } from "@/features/transfers/create-transfer-branch-to-branch/types/createTransfer.types";

function appendTransferStatusParams(
  queryParams: URLSearchParams,
  status?: string[],
) {
  const filteredStatus = status?.filter((st) => st !== "all") ?? [];
  filteredStatus.forEach((st, index) => {
    queryParams.append(`status[${index}]`, st);
  });
}

function appendBranchIdParam(queryParams: URLSearchParams, branch_id?: string) {
  if (!branch_id) return;
  queryParams.set("branch_id", branch_id);
}

export const transfersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTransfersHelpData: builder.query<TransfersHelpData, void>({
      query: () => Endpoints.transfers.getHelpData,
      transformResponse: (
        response: ApiResponse<TransfersHelpData>,
      ): TransfersHelpData => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load transfers help data",
          );
        }
        return response.data;
      },
      providesTags: ["Transfers"],
    }),

    cancelTransfer: builder.mutation<
      { success: boolean; message: string },
      string | number
    >({
      query: (id) => ({
        url: Endpoints.transfers.changeTransferStatus(id),
        method: "POST",
        body: {
          status: "canceled",
        },
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): { success: boolean; message: string } => {
        if (!response.success) {
          throw new Error(response.message || "Failed to cancel transfer");
        }
        return { success: response.success, message: response.message ?? "" };
      },
      invalidatesTags: ["Transfers"],
    }),
    rejectTransfer: builder.mutation<
      ApiResponse<unknown>,
      { id: string | number; reject_reason: string }
    >({
      query: ({ id, reject_reason }) => ({
        url: Endpoints.transfers.changeTransferStatus(id),
        method: "POST",
        body: {
          status: "rejected",
          reject_reason,
        },
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): ApiResponse<unknown> => {
        if (!response.success) {
          throw new Error(response.message || "Failed to reject transfer");
        }
        return response;
      },
      invalidatesTags: ["Transfers"],
    }),
    getMyTransfersIn: builder.query<TransfersListResult, TransfersListParams>({
      query: ({
        page = 1,
        per_page = 10,
        search,
        status,
        branch_id,
        creation_date_from,
        creation_date_to,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("page", String(page));
        queryParams.set("per_page", String(per_page));
        queryParams.set("sort_by", "created_date");
        queryParams.set("sort_dir", "desc");
        if (search) {
          queryParams.set("search", search);
        }
        appendTransferStatusParams(queryParams, status);
        appendBranchIdParam(queryParams, branch_id);
        if (creation_date_from) {
          queryParams.set("creation_date_from", creation_date_from);
        }
        if (creation_date_to) {
          queryParams.set("creation_date_to", creation_date_to);
        }
        return `${Endpoints.transfers.getMyTransfersIn}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ApiTransfersListData>,
      ): TransfersListResult => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load my transfers in");
        }

        const transfersData = response.data;
        return {
          data: transfersData.data ?? [],
          pagination: {
            page: transfersData.current_page ?? 1,
            limit: transfersData.per_page ?? 10,
            total: transfersData.total ?? 0,
            totalPages: transfersData.total_pages ?? 1,
          },
        };
      },
      providesTags: ["Transfers"],
    }),
    getMyTransferInById: builder.query<
      TransferDetailsResponse,
      string | number
    >({
      query: (id) => Endpoints.transfers.getMyTransferInById(id),
      transformResponse: (
        response: ApiResponse<TransferDetailsResponse>,
      ): TransferDetailsResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load my transfer in");
        }
        return response.data;
      },
      providesTags: (_result, _error, id) => [
        { type: "Transfers", id: String(id) },
      ],
    }),
    getMyTransfersOut: builder.query<TransfersListResult, TransfersListParams>({
      query: ({
        page = 1,
        per_page = 10,
        search,
        status,
        branch_id,
        creation_date_from,
        creation_date_to,
        delivery_date_from,
        delivery_date_to,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("page", String(page));
        queryParams.set("per_page", String(per_page));
        queryParams.set("sort_by", "created_date");
        queryParams.set("sort_dir", "desc");
        if (search) {
          queryParams.set("search", search);
        }
        appendTransferStatusParams(queryParams, status);
        appendBranchIdParam(queryParams, branch_id);
        if (creation_date_from) {
          queryParams.set("creation_date_from", creation_date_from);
        }
        if (creation_date_to) {
          queryParams.set("creation_date_to", creation_date_to);
        }
        if (delivery_date_from) {
          queryParams.set("delivery_date_from", delivery_date_from);
        }
        if (delivery_date_to) {
          queryParams.set("delivery_date_to", delivery_date_to);
        }
        return `${Endpoints.transfers.getMyTransfersOut}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ApiTransfersListData>,
      ): TransfersListResult => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load my transfers in");
        }

        const transfersData = response.data;
        return {
          data: transfersData.data ?? [],
          pagination: {
            page: transfersData.current_page ?? 1,
            limit: transfersData.per_page ?? 10,
            total: transfersData.total ?? 0,
            totalPages: transfersData.total_pages ?? 1,
          },
        };
      },
      providesTags: ["Transfers"],
    }),
    getMyTransferOutById: builder.query<
      TransferDetailsResponse,
      string | number
    >({
      query: (id) => Endpoints.transfers.getMyTransferOutById(id),
      transformResponse: (
        response: ApiResponse<TransferDetailsResponse>,
      ): TransferDetailsResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load my transfer in");
        }
        return response.data;
      },
      providesTags: (_result, _error, id) => [
        { type: "Transfers", id: String(id) },
      ],
      keepUnusedDataFor: 0,
    }),
    receiveTransfer: builder.mutation<
      ApiResponse<unknown>,
      { id: string | number; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: Endpoints.transfers.receiveTransfer(id),
        method: "POST",
        body: data,
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): ApiResponse<unknown> => {
        if (!response.success) {
          throw new Error(response.message || "Failed to receive transfer");
        }
        return response;
      },
      invalidatesTags: ["Transfers"],
    }),
    getItemsDetails: builder.query<
      ItemsDetailsResponse[],
      GetItemsDetailsParams
    >({
      query: ({ item_ids }) => {
        const queryParams = new URLSearchParams();

        item_ids.forEach((item_id, index) => {
          queryParams.append(`item_ids[${index}]`, item_id);
        });

        return `${Endpoints.transfers.getItemsDetails}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ItemsDetailsResponse[]>,
      ): ItemsDetailsResponse[] => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase orders");
        }

        return response.data;
      },
      providesTags: ["Transfers"],
    }),
    createTransfer: builder.mutation<
      ApiResponse<unknown>,
      CreateTransferRequestPayload
    >({
      query: (data) => ({
        url: Endpoints.transfers.createTransfer,
        method: "POST",
        body: data,
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): ApiResponse<unknown> => {
        if (!response.success) {
          throw new Error(response.message || "Failed to create transfer");
        }
        return response;
      },
      invalidatesTags: ["Transfers"],
    }),

    showTransferDraft: builder.query<CreateTransferRequestPayload, void>({
      query: () => ({
        url: Endpoints.transfers.showDraft,
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<CreateTransferRequestPayload>,
      ): CreateTransferRequestPayload => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase orders");
        }

        return response?.data;
      },
      providesTags: ["Transfers"],
    }),
    deleteTransferDraft: builder.mutation<ApiResponse<unknown>, void>({
      query: () => ({
        url: Endpoints.transfers.deleteDraft,
        method: "DELETE",
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): ApiResponse<unknown> => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to delete transfer draft",
          );
        }
        return response;
      },
      invalidatesTags: ["Transfers"],
    }),
  }),
});

export const {
  useGetMyTransfersInQuery,
  useGetMyTransferInByIdQuery,
  useGetTransfersHelpDataQuery,
  useCancelTransferMutation,
  useRejectTransferMutation,
  useReceiveTransferMutation,
  useGetItemsDetailsQuery,
  useLazyGetItemsDetailsQuery,
  useCreateTransferMutation,
  useShowTransferDraftQuery,
  useGetMyTransfersOutQuery,
  useGetMyTransferOutByIdQuery,
  useDeleteTransferDraftMutation,
} = transfersApi;
